import { useState, useEffect, useMemo } from 'react'
import type { PaymentMethod, BillingAddress, CreditCardPaymentData } from '@/types/payment'
import { useReferenceData } from '@/hooks/useReferenceData'
import { getRemainingAmount, calculatePaymentFee, validatePaymentAmount } from '@/lib/utils/paymentCalculations'
import { formatApiError, showWarning } from '@/lib/utils/notifications'
import { getStreetTypesCached, type StreetType } from '@/lib/api/streetsService'
import type { PlaceResult } from '@/hooks/useGooglePlacesAutocomplete'
import {
  TRUSTWAVE_SEAL_URLS,
} from '@/constants/trustwave'
import type { PaymentFormProps, PaymentFormAutoFilled, PaymentFormGuard } from './paymentFormTypes'
import {
  applyPlaceToBillingAddress,
  autoFilledFromBilling,
  buildBillingFromCurrentAddress,
  EMPTY_BILLING_ADDRESS,
  normalizeStateCode,
} from './paymentFormAddressUtils'
import { validatePaymentFormFields } from './paymentFormValidation'

export function usePaymentForm({
  paymentConfig,
  applicationData,
  applicationFee,
  paymentType = 'APPLICATION_FEE',
  onPaymentSubmit,
}: PaymentFormProps) {
  const referenceData = useReferenceData()
  const states = useMemo(
    () => (referenceData?.states || []) as Array<{ state_code: string; state_name: string }>,
    [referenceData?.states],
  )

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CARDCONNECT')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cardNumber, setCardNumber] = useState('')
  const [expiryMonth, setExpiryMonth] = useState('')
  const [expiryYear, setExpiryYear] = useState('')
  const [cvv, setCvv] = useState('')
  const [billingAddressDifferent, setBillingAddressDifferent] = useState(false)
  const [prepaidCard, setPrepaidCard] = useState(false)
  const [isPoBox, setIsPoBox] = useState(false)
  const [showAptTooltip, setShowAptTooltip] = useState(false)
  const [showNameInfoModal, setShowNameInfoModal] = useState(false)
  const [billingAddress, setBillingAddress] = useState<BillingAddress>(EMPTY_BILLING_ADDRESS)
  const [paymentAmount, setPaymentAmount] = useState<string>('')
  const [paymentAmountError, setPaymentAmountError] = useState<string>('')
  const [usePartialPayment, setUsePartialPayment] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [streetTypes, setStreetTypes] = useState<StreetType[]>([])
  const [isAutoFilled, setIsAutoFilled] = useState<PaymentFormAutoFilled>({
    firstName: false,
    lastName: false,
    houseNo: false,
    street: false,
    apartment: false,
    city: false,
    state: false,
    zipCode: false,
  })
  const [hasInitializedPartialPayment, setHasInitializedPartialPayment] = useState(false)

  useEffect(() => {
    let isMounted = true
    const fetchStreets = async () => {
      try {
        const data = await getStreetTypesCached()
        if (isMounted) setStreetTypes(data)
      } catch (err) {
        showWarning(formatApiError(err))
      }
    }
    void fetchStreets()

    const scriptId = 'trustwave-seal-script'
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script')
      script.id = scriptId
      script.type = 'text/javascript'
      script.src = TRUSTWAVE_SEAL_URLS.sealScript
      document.body.appendChild(script)
    }

    return () => {
      isMounted = false
      const script = document.getElementById(scriptId)
      if (script && document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  const handleAddressSelect = (place: PlaceResult) => {
    setBillingAddress((prev) => applyPlaceToBillingAddress(place, prev, streetTypes))
  }

  useEffect(() => {
    if (!applicationData) return

    const firstNameValue =
      applicationData.applicant_first_name ||
      applicationData.first_name ||
      applicationData.user_data?.first_name ||
      ''
    const lastNameValue =
      applicationData.applicant_last_name ||
      applicationData.last_name ||
      applicationData.user_data?.last_name ||
      ''

    // eslint-disable-next-line react-hooks/set-state-in-effect -- prefill cardholder name and billing address from application
    setFirstName(firstNameValue)
    setLastName(lastNameValue)
    setIsAutoFilled((prev) => ({
      ...prev,
      firstName: !!firstNameValue,
      lastName: !!lastNameValue,
    }))

    if (applicationData.current_address) {
      const billingAddr = buildBillingFromCurrentAddress(applicationData.current_address, states)
      setBillingAddress(billingAddr)
      setIsAutoFilled((prev) => ({
        ...prev,
        ...autoFilledFromBilling(billingAddr),
      }))
    } else {
      setBillingAddress(EMPTY_BILLING_ADDRESS)
    }
  }, [applicationData, states])

  useEffect(() => {
    if (billingAddress.state && states.length > 0) {
      const normalized = normalizeStateCode(billingAddress.state, states)
      if (normalized !== billingAddress.state) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- normalize state code when reference data loads
        setBillingAddress((prev) => ({ ...prev, state: normalized }))
      }
    }
  }, [states, billingAddress.state])

  useEffect(() => {
    if (!paymentConfig) return
    /* eslint-disable react-hooks/set-state-in-effect -- default payment method from gateway config */
    if (paymentConfig.cardconnect?.status) {
      setPaymentMethod('CARDCONNECT')
    } else if (paymentConfig.paypal?.status) {
      setPaymentMethod('Paypal')
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [paymentConfig])

  const isContractPayment = paymentType === 'CONTRACT' || paymentType === 'DEFAULT_INVOICE'
  const allowPartialPayment = paymentType === 'CONTRACT'
  const remainingAmount = isContractPayment ? getRemainingAmount(applicationData) : 0

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- seed or clear partial amount when mode toggles */
    if (isContractPayment && usePartialPayment && remainingAmount > 0) {
      if (!hasInitializedPartialPayment) {
        setPaymentAmount(remainingAmount.toFixed(2))
        setHasInitializedPartialPayment(true)
      }
    } else if (isContractPayment && !usePartialPayment) {
      setPaymentAmount('')
      setHasInitializedPartialPayment(false)
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [usePartialPayment, isContractPayment, remainingAmount, hasInitializedPartialPayment])

  const productFee =
    paymentType === 'APPLICATION_FEE'
      ? applicationData?.application_fee !== undefined && applicationData?.application_fee !== null
        ? Number(applicationData.application_fee)
        : applicationFee !== null && applicationFee !== undefined
          ? applicationFee
          : null
      : usePartialPayment && paymentAmount
        ? Number(paymentAmount)
        : remainingAmount

  const paymentFee =
    isContractPayment && paymentConfig && paymentMethod
      ? calculatePaymentFee(
          productFee || 0,
          remainingAmount,
          paymentConfig,
          paymentMethod,
          applicationData,
        )
      : null

  const totalAmount = paymentFee ? paymentFee.total_amount : productFee || 0

  const validateForm = (): boolean => {
    const newErrors = validatePaymentFormFields({
      paymentMethod,
      firstName,
      lastName,
      cardNumber,
      expiryMonth,
      expiryYear,
      cvv,
      billingAddress,
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePaymentAmountChange = (value: string) => {
    setPaymentAmount(value)
    setPaymentAmountError('')
    if (isContractPayment && value) {
      const amount = Number(value)
      const validationError = validatePaymentAmount(amount, remainingAmount, prepaidCard)
      if (validationError) {
        setPaymentAmountError(validationError)
      }
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (productFee === null || productFee <= 0) {
      if (isContractPayment) {
        setPaymentAmountError('Please enter a payment amount')
      }
      return
    }

    if (isContractPayment) {
      if (usePartialPayment && paymentAmount) {
        const amount = Number(paymentAmount)
        const validationError = validatePaymentAmount(amount, remainingAmount, prepaidCard)
        if (validationError) {
          setPaymentAmountError(validationError)
          return
        }
      } else if (!usePartialPayment && prepaidCard) {
        setPaymentAmountError(
          'This payment method is intended for cards with limits, and should not be used for the full amount. Please use the "Use Partial Payment Method" option or turn off the pre-paid card option.',
        )
        return
      }
    }

    if (!validateForm()) return

    const paymentData: CreditCardPaymentData = {
      first_name: firstName,
      last_name: lastName,
      card_number: cardNumber.replace(/\D/g, ''),
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
      cvv,
      billing_address: billingAddress,
      address_change: billingAddressDifferent,
      prepaid_card: prepaidCard,
    }

    const paymentAmountOverride =
      allowPartialPayment && usePartialPayment && paymentAmount
        ? Number(paymentAmount)
        : undefined
    onPaymentSubmit(paymentData, paymentMethod, paymentAmountOverride)
  }

  const guard: PaymentFormGuard = (() => {
    if (!paymentConfig) return 'loading_config'
    if (!applicationData) return 'loading_application'
    if (paymentType === 'APPLICATION_FEE' && productFee === null) return 'no_application_fee'
    if (isContractPayment && remainingAmount <= 0) return 'no_remaining'
    const hasCardConnect = paymentConfig.cardconnect?.status === true
    const hasPayPal = paymentConfig.paypal?.status === true
    if (!hasCardConnect && !hasPayPal) return 'no_methods'
    return null
  })()

  return {
    paymentConfig,
    applicationData,
    paymentType,
    paymentMethod,
    setPaymentMethod,
    states,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    cardNumber,
    setCardNumber,
    expiryMonth,
    setExpiryMonth,
    expiryYear,
    setExpiryYear,
    cvv,
    setCvv,
    billingAddress,
    setBillingAddress,
    billingAddressDifferent,
    setBillingAddressDifferent,
    prepaidCard,
    setPrepaidCard,
    isPoBox,
    setIsPoBox,
    showAptTooltip,
    setShowAptTooltip,
    showNameInfoModal,
    setShowNameInfoModal,
    paymentAmount,
    paymentAmountError,
    usePartialPayment,
    setUsePartialPayment,
    setPaymentAmount,
    setPaymentAmountError,
    errors,
    streetTypes,
    isAutoFilled,
    isContractPayment,
    allowPartialPayment,
    remainingAmount,
    productFee,
    paymentFee,
    totalAmount,
    handleAddressSelect,
    handlePaymentAmountChange,
    handleSubmit,
    guard,
  }
}

export type PaymentFormHookState = ReturnType<typeof usePaymentForm>
